import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MovieSearchModal from '../components/MovieSearchModal';
import { getUserDictionaries, Dictionary as FirebaseDictionary, saveDictionary, DictionaryEntry } from '../firebase';
// Material UI imports
import { 
  Box, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  CardActionArea,
  Chip,
  Container,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import { Add as AddIcon, Logout as LogoutIcon } from '@mui/icons-material';

interface Dictionary {
  id: string;
  title: string;
  language: string;
  difficulty: string;
  speechStyle: string;
  moviePoster?: string;
  year?: string;
}

interface MovieResult {
  id: string;
  title: string;
  image?: string;
  year?: string;
  isSeries?: boolean;
  description?: string;
  contentRating?: string;
  seasonNumber?: number;
  episodeNumber?: number;
  episodeTitle?: string;
  subtitles?: string;
  selectedLanguage?: string;
  dictionaryEntries?: DictionaryEntry[];
  cefrLevel?: string;
  speechStyle?: string;
}

const Dashboard: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [showMovieModal, setShowMovieModal] = useState(false);
  const [dictionaries, setDictionaries] = useState<Dictionary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDictionaries = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        const result = await getUserDictionaries(currentUser.uid);
        
        if (result.error) {
          setError(result.error);
        } else {
          // Convert Firebase dictionaries to the local format
          const formattedDictionaries = result.dictionaries.map(dict => ({
            id: dict.id || '',
            title: dict.title,
            language: dict.targetLanguage,
            difficulty: dict.cefrLevel,
            speechStyle: dict.speechStyle,
            moviePoster: dict.image,
            year: dict.year
          }));
          
          setDictionaries(formattedDictionaries);
        }
      } catch (err: any) {
        setError(err.message || 'An error occurred');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDictionaries();
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const handleAddDictionary = () => {
    setShowMovieModal(true);
  };

  const handleCloseModal = () => {
    setShowMovieModal(false);
  };
  
  const handleSelectMovie = async (movie: MovieResult) => {
    if (!currentUser || !movie.dictionaryEntries) return;
    
    try {
      setLoading(true);
      
      // Create a new dictionary in Firestore
      const dictionaryData = {
        userId: currentUser.uid,
        title: movie.title,
        year: movie.year,
        imdbId: movie.id,
        image: movie.image,
        targetLanguage: movie.selectedLanguage === 'en' ? 'English' : 
                        movie.selectedLanguage === 'ru' ? 'Русский' : 
                        movie.selectedLanguage === 'es' ? 'Español' : 'Français',
        cefrLevel: movie.cefrLevel || 'B1',
        speechStyle: movie.speechStyle || 'Colloquial',
        subtitlesLanguage: movie.selectedLanguage || 'en',
        entries: movie.dictionaryEntries
      };
      
      const saveResult = await saveDictionary(dictionaryData);
      
      if (saveResult.error) {
        setError(saveResult.error);
      } else if (saveResult.id) {
        // Navigate to the dictionary detail page
        navigate(`/dictionary/${saveResult.id}`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save dictionary');
    } finally {
      setLoading(false);
      setShowMovieModal(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" color="default" elevation={2}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700, color: 'primary.main' }}>
            Добро пожаловать в LinguaSub
          </Typography>
          <IconButton onClick={handleLogout} color="primary" aria-label="logout">
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4
        }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Мои словари
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={handleAddDictionary}
            disabled={loading}
          >
            Добавить словарь
          </Button>
        </Box>
        
        <Typography variant="h6" sx={{ mb: 3, color: 'text.secondary' }}>
          {dictionaries.length > 0 
            ? `У вас ${dictionaries.length} ${dictionaries.length === 1 ? 'словарь' : 
               dictionaries.length > 1 && dictionaries.length < 5 ? 'словаря' : 'словарей'}`
            : 'У вас пока нет словарей'}
        </Typography>
        
        {loading && dictionaries.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        ) : dictionaries.length > 0 ? (
          <Grid container spacing={3}>
            {dictionaries.map((dictionary) => (
              <Grid item key={dictionary.id} xs={12} sm={6} md={4}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardActionArea onClick={() => navigate(`/dictionary/${dictionary.id}`)}>
                    <CardMedia
                      component="img"
                      height="180"
                      image={dictionary.moviePoster || '/placeholder-movie.png'}
                      alt={dictionary.title}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent>
                      <Typography variant="h6" component="div" gutterBottom>
                        {dictionary.title}
                      </Typography>
                      {dictionary.year && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {dictionary.year}
                        </Typography>
                      )}
                      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip 
                          label={dictionary.language} 
                          size="small" 
                          color="primary" 
                          variant="outlined" 
                        />
                        <Chip 
                          label={dictionary.difficulty} 
                          size="small" 
                          color="secondary" 
                          variant="outlined" 
                        />
                        <Chip 
                          label={dictionary.speechStyle} 
                          size="small" 
                          color="info" 
                          variant="outlined" 
                        />
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ 
            textAlign: 'center', 
            py: 6, 
            border: '1px dashed',
            borderColor: 'grey.300', 
            borderRadius: 2,
            bgcolor: 'grey.50'
          }}>
            <Typography variant="h6" gutterBottom>
              Создайте свой первый словарь
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Нажмите на кнопку "Добавить словарь" чтобы начать
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              onClick={handleAddDictionary}
              sx={{ mt: 2 }}
            >
              Добавить словарь
            </Button>
          </Box>
        )}
      </Container>

      {showMovieModal && (
        <MovieSearchModal 
          onClose={handleCloseModal} 
          onSelectMovie={handleSelectMovie}
        />
      )}
    </Box>
  );
};

export default Dashboard; 